<template>
    <div ref="onboardingEmail" class="listing-layout flex-column pb-8">
        <!-- LOADER -->
        <preloader :background="false" :display="loading" class="preloader prelative" />

        <!-- HEADER -->
        <header class="flex-level mb-4 mt-1">
            <!-- TITLE -->
            <h1 class="m-0">{{ $l10n.__('Email') }}</h1>
        </header>

        <!-- BODY -->
        <div class="flex">
            <div class="container-medium m-auto flex">
                <el-collapse v-model="step" accordion class="collapse-onboarding-mail m-auto">
                    <el-collapse-item :name="1">
                        <template #title>
                            1/ {{ $l10n.__('Sélectionnez votre service email') }}
                            <span v-if="provider" class="fs-3 regular">{{ provider.title }}</span>
                        </template>
                        <EmailOnboardingProvider
                            :successUrl="getSuccessUrl"
                            @validProvider="onValidProvider"
                        />
                    </el-collapse-item>
                    <el-collapse-item
                        :disabled="!provider || isEmailEngineOtherProviderSelectd"
                        :name="2"
                    >
                        <template #title>
                            2/ {{ $l10n.__('Sélectionnez votre mode') }}
                            <span v-if="mode" class="fs-3 regular">{{ mode.title }}</span>
                        </template>
                        <EmailOnboardingMode
                            v-bind="params"
                            :provider="provider"
                            @validMode="onValidMode"
                        />
                    </el-collapse-item>
                    <el-collapse-item :disabled="isStepCheckingEmailDisabled" :name="3">
                        <template #title>
                            3/ {{ $l10n.__('Vérifiez votre adresse email') }}
                            <span
                                v-if="emailIsVerified && !isStepCheckingEmailDisabled"
                                class="fs-3 regular"
                            >
                                {{ senderEmail }} {{ $l10n.__('(vérifié)') }}
                            </span>
                        </template>
                        <EmailOnboardingCheckEmail
                            v-if="!isStepCheckingEmailDisabled"
                            :sentFromCorp="sentFromCorp"
                            @onClickContinue="goToStep4"
                            @onEmailSubmitted="onEmailSubmitted"
                        />
                    </el-collapse-item>
                    <el-collapse-item
                        :disabled="!emailIsVerified || !isOtherProviderSelected"
                        :name="4"
                    >
                        <template #title>
                            4/ {{ $l10n.__('Authentifiez votre domaine') }}
                            <span class="fs-3 regular">{{ domain }}</span>
                        </template>
                        <EmailOnboardingDomain class="mx-5" @validDomain="onValidDomain" />
                    </el-collapse-item>
                </el-collapse>
            </div>
        </div>

        <!-- POPUP -->
        <CompassModal
            v-model:visible="confirmationModal.active"
            size="small"
            :title="confirmationModal.title"
            class="onboarding-email"
        >
            <template #default>
                <div class="dialog__default">
                    <p>{{ confirmationModal.description }}</p>
                </div>
            </template>
            <template #footer>
                <div class="dialog__footer">
                    <CompassButton
                        :label="confirmationModal.secondaryBtn.label"
                        type="secondary"
                        @click="confirmationModal.secondaryBtn.action()"
                    />
                    <CompassButton
                        :label="confirmationModal.primaryBtn.label"
                        class="ml-3"
                        @click="confirmationModal.primaryBtn.action()"
                    />
                </div>
            </template>
        </CompassModal>

        <SetUpProviderModal
            :isVisible="isSetUpProviderModalVisible"
            @close="handleCloseSetupProviderModal"
        />
    </div>
</template>

<script>
import Preloader from 'vcomponents/utilities/Preloader.vue';
import EmailOnboardingProvider from 'vcomponents/email/EmailOnboardingProvider.vue';
import EmailOnboardingMode from 'vcomponents/email/EmailOnboardingMode.vue';
import EmailOnboardingCheckEmail from 'vcomponents/email/EmailOnboardingCheckEmail.vue';
import EmailOnboardingDomain from 'vcomponents/email/EmailOnboardingDomain.vue';
import OnboardingEmailApi from 'api/onboarding/email/onboardingEmail.js';
import LocalStorage from 'Core/commons/LocalStorage';
import { createNamespacedHelpers } from 'vuex';
import _ from 'lodash';
import onboardingEmail from 'js/vue/api/onboarding/email/onboardingEmail.js';
import { providers, modes } from 'Config/authenticated/dictionary/onboardingEmail.js';
import Feature from 'Services/Feature';
import SetUpProviderModal from 'vcomponents/email/SetUpProviderModal.vue';

const { mapState: mapStateUser, mapActions: mapActionsUser } = createNamespacedHelpers('user');

export default {
    name: 'OnboardingEmail',
    components: {
        Preloader,
        EmailOnboardingProvider,
        EmailOnboardingMode,
        EmailOnboardingDomain,
        EmailOnboardingCheckEmail,
        SetUpProviderModal,
    },
    data() {
        return {
            loading: false,
            step: null,
            provider: null,
            mode: null,
            domain: null,
            confirmationModal: {
                active: false,
                title: '',
                description: '',
                secondaryBtn: {
                    action: () => (this.confirmationModal.active = false),
                    label: this.$l10n.__('Annuler'),
                },
                primaryBtn: {
                    action: () => (this.confirmationModal.active = false),
                    label: this.$l10n.__('Valider'),
                },
            },
            params: null,
            hasEmailVerifiedEvent: false,
            sentFromCorp: false,
            isSetUpProviderModalVisible: false,
        };
    },
    computed: {
        ...mapStateUser(['senderEmail', 'emailIsVerified', 'email']),
        /**
         * Return context where user start onboarding or route of email listing
         */
        context() {
            return this.$attrs.context ? decodeURIComponent(this.$attrs.context) : '/?_f=email';
        },
        isStepCheckingEmailDisabled() {
            return this.mode === null || this.provider === null || this.mode?.id !== 'transfert';
        },
        isEnabledEmailEngine() {
            return Feature.isEnabled('email-engine');
        },
        isEnabledNylasImapAuth() {
            return Feature.isEnabled('nylas-imap-auth') && Feature.isEnabled('nylas-api-v3');
        },
        isOtherProviderSelected() {
            return this.provider ? this.provider.id === 'other' : false;
        },
        isEmailEngineOtherProviderSelectd() {
            return this.isEnabledEmailEngine && this.isOtherProviderSelected;
        },
        getSuccessUrl() {
            return this.$route.meta.previousRoute ?? '/';
        },
    },
    mounted() {
        this.isSetUpProviderModalVisible = this.$route.query.invalid_onboarding
            ? this.$route.query.invalid_onboarding === 'transfer'
            : false;
    },
    /**
     * On created component,
     *  - Add specific class on body element
     *  - Call legacy back for init onboarding email (in bdd reset onboarding_setup to 'N')
     *    then initOnboardingEmailStorage and if provider exists, call method "onValidProvider" for pass directly to step 2
     */
    created() {
        document.body.className = '';
        document.body.classList.add('page-onboarding-email');
        if (!_.isEmpty(this.$route.query) && this.$route.query.step === '3') {
            const params = this.$route.query;
            this.step = +params.step;
            onboardingEmail
                .validateEmail(params.senderEmail)
                .then(({ verified }) => {
                    if (!verified) {
                        const message =
                            this.$route.query.invalid_onboarding === 'expired'
                                ? this.$l10n.__(
                                    'Ce lien de validation est expiré, veuillez réessayer',
                                )
                                : this.$l10n.__(
                                    'Une erreur est survenue, veuillez ré-essayer ultérieurement.',
                                );
                        this.displayToast('error', message);
                    } else {
                        this.displayToast('success', this.$l10n.__('Adresse email vérifiée'));
                        this.provider = providers.find(
                            (provider) => provider.id === params.provider,
                        );
                        this.goToStep4();
                    }
                    this.provider = providers.find((provider) => provider.id === params.provider);
                    this.mode = modes.find((mode) => mode.id === params.mode);
                })
                .catch(() => {
                    this.displayToast(
                        'error',
                        this.$l10n.__(
                            "Échec de vérification de l'adresse email, merci de réessayer",
                        ),
                    );
                });
        } else {
            if (
                this.$route.query.invalid_onboarding &&
                this.$route.query.invalid_onboarding !== 'transfer'
            ) {
                const message =
                    this.$route.query.invalid_onboarding === 'expired'
                        ? this.$l10n.__('Ce lien de validation est expiré, veuillez réessayer')
                        : this.$l10n.__(
                            'Une erreur est survenue, veuillez ré-essayer ultérieurement.',
                        );
                this.displayToast('error', message);
            }
            OnboardingEmailApi.initValidateOnboardingEmail()
                .then(() => {
                    this.initOnboardingEmailStorage();
                    this.updateEmailIsVerified(false);
                    if (this.$attrs.provider) {
                        this.onValidProvider(this.$attrs.provider);
                    }
                })
                .finally(() => {
                    this.step = 1;
                });
        }
        document.addEventListener('visibilitychange', () => this.onVisibilityChange());
        EventBus.on('email:onEmailVerified', this.onEmailVerifiedEvent);
    },
    /**
     * On destroyed component,
     *  - Delete specific class on body element
     *
     */
    unmounted() {
        document.body.classList.remove('page-onboarding-email');
        document.removeEventListener('visibilitychange', () => this.onVisibilityChange());
        EventBus.off('email:onEmailVerified', this.onEmailVerifiedEvent);
    },
    methods: {
        ...mapActionsUser(['updateSenderEmail', 'sendVerificationEmail', 'updateEmailIsVerified']),
         
        onEmailVerifiedEvent(event) {
            this.hasEmailVerifiedEvent = true;
            this.eventData = event;
            if (document.visibilityState === 'visible') {
                this.triggerEventEmailVerified();
            }
        },
        onVisibilityChange() {
            if (document.visibilityState === 'visible' && this.hasEmailVerifiedEvent) {
                this.triggerEventEmailVerified();
            }
        },
        triggerEventEmailVerified() {
            if (!this.isStepCheckingEmailDisabled) {
                if (this.eventData.success) {
                    this.updateEmailIsVerified(true);
                    this.updateSenderEmail(this.eventData.senderEmail);
                    this.goToStep4();
                }
                this.displayToast(
                    this.eventData.success ? 'success' : 'error',
                    this.eventData.message,
                );
                this.hasEmailVerifiedEvent = false;
            }
        },
        /**
         * Set in storage that onboarding email is actived
         * Needed for EmailListingDomainAuthBanner.vue to detects if the user has gone through this onboarding email V2
         *
         * @return {void}
         */
        initOnboardingEmailStorage() {
            const onboardingEmailStorage = new LocalStorage('onboardingEmail', 'V2');
            onboardingEmailStorage.set(true);
        },
        /**
         * When user valid the choice of the provider
         * set provider and pass to step 2
         *
         * @param {object} provider
         * @return {void}
         */
        onValidProvider(provider) {
            if (
                this.isEnabledEmailEngine &&
                provider.id === 'other' &&
                !this.isEnabledNylasImapAuth
            ) {
                // Will be redirected to Intercom chat
            } else {
                OnboardingEmailApi.validateOnboardingEmailStep(2, { provider: provider.id })
                    .then(({ onboarding_params }) => {
                        this.params = onboarding_params;
                        this.provider = provider;
                        this.step = 2;
                    })
                    .catch(() =>
                        this.displayToast('error', this.$l10n.__('Une erreur est survenue')),
                    );
            }
        },
        /**
         * When user valid the choice of the mode
         * performs actions according to the mode an provider
         *
         * @param {object} mode
         * @return {void}
         */
        onValidMode(mode) {
            switch (`${mode.id}::${this.provider.id}`) {
                case 'sync::gmail':
                    window.location.href = '/?_f=prefsNylas&action=oauthGoogle';
                    break;
                case 'sync::office365':
                    window.location.href = '/?_f=prefsNylas&action=oauthMicrosoftOffice365';
                    break;
                case 'sync::other':
                    window.location.href = this.params.urlAuth;
                    break;
                case 'transfert::other':
                    this.validateOnboardingEmailStep(mode);
                    break;
                case 'transfert::gmail':
                case 'transfert::office365':
                    this.confirmationModal = {
                        ...this.confirmationModal,
                        active: true,
                        title: this.$l10n.__('Êtes-vous sûr de votre choix ?'),
                        description: this.$l10n.__(
                            'La sécurité des emails est en constante évolution et en choisissant le mode transfert avec %(provider)s vous prenez le risque que certains ou la totalité de vos emails ne soient pas reçus par vos destinataires.',
                            { provider: this.provider.title },
                        ),
                        secondaryBtn: {
                            action: () => (this.confirmationModal.active = false),
                            label: this.$l10n.__('Modifier mon choix'),
                        },
                        primaryBtn: {
                            action: () => this.validateOnboardingEmailStep(mode),
                            label: this.$l10n.__('Je confirme mon choix'),
                        },
                    };
                    break;
            }
        },
        /**
         * When the user validates the choice on the configuration
         * performs actions depending on whether the configuration is applied
         *
         * @param {{appliedConf: boolean}} {appliedConf}
         * @return {void}
         */
        onValidDomain({ appliedConf }) {
            appliedConf
                ? this.validateOnboardingEmail(this.goToContext)()
                : (this.confirmationModal = {
                    ...this.confirmationModal,
                    active: true,
                    title: this.$l10n.__(
                        'Êtes-vous sûr de remettre à plus tard cette opération ?',
                    ),
                    description: this.$l10n.__(
                        'En décidant de ne pas procéder immédiatement à la vérification de votre domaine, vous prenez le risque que certains ou la totalité de vos emails ne soient pas reçus par vos destinataires.',
                    ),
                    secondaryBtn: {
                        action: this.validateOnboardingEmail(this.goToContext),
                        label: this.$l10n.__("J'ai compris"),
                    },
                    primaryBtn: {
                        action: () => (this.confirmationModal.active = false),
                        label: this.$l10n.__('Authentifier mon domaine'),
                    },
                });
        },
        /**
         * Validate Step 2 and 3 with legacy back (necessary for fill bdd onboarding_log and the good proceedings)
         * Then launch callback
         *
         * @param {function} callback goToFinalPage() or goToContext()
         * @return {function}
         */
        validateOnboardingEmail(callback) {
            return () => {
                new Promise((resolve) => resolve())
                    .then(() =>
                        OnboardingEmailApi.validateOnboardingEmailStep(4, { mode: 'simple' }),
                    )
                    .then(() => callback())
                    .catch(() =>
                        this.displayToast('error', this.$l10n.__('Une erreur est survenue')),
                    );
            };
        },
        validateOnboardingEmailStep(mode) {
            OnboardingEmailApi.validateOnboardingEmailStep(3)
                .then(({ email_params }) => {
                    this.sentFromCorp = email_params.sentFromCorp;
                    this.mode = mode;
                    this.step = 3;
                    this.updateEmailIsVerified(false);
                    this.confirmationModal.active = false;
                })
                .catch(() => this.displayToast('error', this.$l10n.__('Une erreur est survenue')));
        },
        /**
         * Redirect to onboarding email final page
         *
         * @return {void}
         */
         
        goToFinalPage() {
            this.$router.push({
                name: 'onboarding.email.final',
                params: { provider: this.provider, context: this.context },
            });
        },
        /**
         * Redirect to email listing or context where user start onboarding
         *
         * @return {void}
         */
        goToContext() {
            window.location.href = this.context;
        },
        goToStep4() {
            if (this.provider.id !== 'other') {
                this.validateOnboardingEmail(this.goToFinalPage)();
            } else {
                this.updateEmailIsVerified(true);
                this.step = 4;
            }
        },
        onEmailSubmitted(email) {
            const url = new URL(window.location.href);
            const redirectUrl = `${url.protocol}//${url.host}${url.pathname}?step=3&senderEmail=${email}&mode=${this.mode.id}&provider=${this.provider.id}`;
            this.sendVerificationEmail({ email, redirectUrl })
                .then((response) => {
                    this.displayToast(response.type, response.message);
                })
                .catch((error) => {
                    this.displayToast(error.type, error.message);
                });
        },
        displayToast(type, text) {
            this.$pushToast({
                type,
                text,
                close: true,
                unique: true,
                icon: true,
                timeout: 4000,
            });
        },
        handleCloseSetupProviderModal() {
            this.isSetUpProviderModalVisible = false;
        },
    },
};
</script>

<style lang="scss">
@use '~@sellsy/styleguide/src/_variables' as *;

.onboarding-email {
    .dialog {
        &__default {
            padding-bottom: var(--spacing-4);
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            padding: 0 var(--spacing-5) var(--spacing-4);
        }
    }
}

.page-onboarding-email #sellsy_app {
    background-color: $white;

    .el-dialog.flex-column {
        border-radius: 4px;
        margin-top: 80px !important;

        .el-dialog__header {
            padding-bottom: 24px;

            .compass-icon-button {
                position: absolute;
                top: 16px;
                right: 16px;
            }
        }
        .el-dialog__footer {
            .dialog-footer.flex-row {
                justify-content: end;
                gap: 16px;
            }
        }
    }
}

.el-collapse.collapse-onboarding-mail {
    border-bottom: none;
    border-top: none;
    width: 976px;

    .el-collapse-item__header,
    .el-collapse-item__wrap,
    .el-collapse-item__content {
        background-color: transparent;
        line-height: 20px;
        i {
            line-height: 20px;
        }
    }

    .el-collapse-item__arrow {
        display: none;
    }

    .el-collapse-item__header {
        align-items: flex-end;
        color: $gray-60;
        display: flex;
        font-size: 17px;
        font-weight: bold;
        height: 60px;
        justify-content: space-between;
        letter-spacing: 0;
        line-height: 21px;
        padding-bottom: 5px;

        &.is-active {
            border-bottom: 1px solid #ebeef5;
            color: $gray-100;
        }
    }

    .el-collapse-item__wrap {
        border-bottom: transparent;
    }

    .el-collapse-item__content {
        margin: 40px auto 40px auto;
    }
}
</style>
